import { GridFilterItem } from '@mui/x-data-grid-pro';
import { httpClient, HttpMethod } from './http-client';
import {
  ActionType,
  AllocatedService,
  BillingType,
  BulkEditServiceDataType,
  EstimatedTimeOfArrivalQuery,
  LinkedServiceData,
  NextServiceStatusesResponse,
  OtherServiceData,
  OtherServiceListData,
  ProcessType,
  ServiceAccordianData,
  ServiceAttribute,
  ServiceBillingInfo,
  ServiceCommonType,
  ServiceCreationObject,
  ServiceListResponse,
  ServiceLog,
  ServiceLogHideData,
  ServiceLogResponse,
  ServiceNodeAllocationData,
  ServiceObject,
  ServiceTargetResponse,
  ServiceTemplateResponse,
  // ServiceTypeResponse, // TODO: unused - kept for reference
  ServiceUpdateRequest,
  ServicesNodesAttributes,
  ServiceActivityResponseDataType,
  ServiceAttendanceObject
} from '../@types/service.type';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';

export interface ServiceQueryOptions {
  filterModel: { items?: GridFilterItem[] };
  page: number;
  pageSize: number;
  sortModel?: ReadonlyArray<{ field: string; sort: string | null | undefined }>;
  locationId?: number | string | null;
  includeChildRecords?: boolean | string | null;
  isFromStructures?: boolean | string | null;
  defaultReport?: string;
  multiSearchQuery?: string;
}

export interface ServiceStatusChangeRequest {
  status: string;
  notes?: string;
}

export interface ParentServiceUpdateRequest {
  parentServiceId: number | null;
}

export interface BulkServiceIdsRequest {
  affectedServiceIds: number[];
}

export interface ServiceBulkCreateRequest {
  service: ServiceCreationObject;
  tag?: {
    entityType: string;
    entityId: number;
    freshTags: string[];
    existingTags: number[];
  };
}

export const createService = (service: ServiceCreationObject): Promise<ServiceObject> => {
  return httpClient<ServiceObject>('/services', HttpMethod.Post, service);
};

export const getActionTypes = (): Promise<ActionType[]> => {
  return httpClient<ActionType[]>('/actionTypes', HttpMethod.Get);
};

export const getProcessTypes = (): Promise<ProcessType[]> => {
  return httpClient<ProcessType[]>('/processTypes', HttpMethod.Get);
};

export const getServiceActionProcessTypes = (activeOnly?: boolean): Promise<ServiceCommonType> => {
  return httpClient<ServiceCommonType>(`/service/action/process/types?activeOnly=${activeOnly}`, HttpMethod.Get);
};

export const createServiceLogsForService = (serviceId: number, service: ServiceLog): Promise<ServiceLogResponse> => {
  return httpClient<ServiceLogResponse>(`/serviceLogs/${serviceId}/forService`, HttpMethod.Post, service);
};

export const updateServiceLogsById = (id: number, serviceLog: Partial<ServiceLog> & { serviceId?: number; isHidden?: boolean }): Promise<ServiceLogResponse> => {
  return httpClient<ServiceLogResponse>(`/serviceLogs/${id}`, HttpMethod.Put, serviceLog);
};

export const getAllServiceTargets = (): Promise<ServiceTargetResponse[]> => {
  return httpClient<ServiceTargetResponse[]>('/serviceTargets', HttpMethod.Get);
};

export const getServiceById = (id: number): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/${id}`, HttpMethod.Get);
};

export const updateServiceById = (id: number, data: ServiceUpdateRequest): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/${id}`, HttpMethod.Put, data);
};

// Reported information update only needs these fields
export interface ReportedInfoUpdateRequest {
  referenceNo1?: string;
  referenceNo2?: string;
  referenceNo3?: string;
  reportedSerialNumber?: number;
  reportedFaultAndDescriptionId?: number;
  reportedModelId?: number;
}

export const updateReportedInformationByServiceId = (id: number, data: ReportedInfoUpdateRequest): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/reportedInformation/${id}`, HttpMethod.Put, data);
};

export const getAllServices = (queryOptions: ServiceQueryOptions, columnSelectionItems: ColumnSelectionItem[]): Promise<ServiceListResponse> => {
  const { filterModel, page, pageSize, sortModel, locationId, includeChildRecords, defaultReport, multiSearchQuery } = queryOptions;
  let url = `/servicesList?page=${page + 1}&limit=${pageSize}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = filter?.operator;
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  if (locationId) {
    url += `&locationId=${locationId}`;
  }

  if (includeChildRecords) {
    url += `&includeChildRecords=${includeChildRecords}`;
  }

  if (defaultReport) {
    url += `&defaultReport=${defaultReport}`;
  }

  if (multiSearchQuery) {
    url += `&multiSearchQuery=${multiSearchQuery}`;
  }

  return httpClient<ServiceListResponse>(url, HttpMethod.Post, { columnSelectionItems: columnSelectionItems });
};

export const getServiceLogsByServiceId = (id: number): Promise<ServiceLogResponse[]> => {
  return httpClient<ServiceLogResponse[]>(`/services/${id}/logs`, HttpMethod.Get);
};

export const getServiceLogsById = (id: number): Promise<ServiceLogResponse> => {
  return httpClient<ServiceLogResponse>(`/serviceLogs/${id}`, HttpMethod.Get);
};

export const hideServiceLog = (id: number, nodeType: ServiceLogHideData): Promise<void> => {
  return httpClient<void>(`/servicelogs/${id}/hide`, HttpMethod.Put, nodeType);
};

export const updateBulkServiceList = (serviceType: BulkEditServiceDataType): Promise<void> => {
  return httpClient<void>('/serviceListBulkUpdate', HttpMethod.Patch, serviceType);
};

export const updateBulkServiceEditList = (serviceType: BulkEditServiceDataType): Promise<void> => {
  return httpClient<void>('/serviceEditListBulkUpdate', HttpMethod.Patch, serviceType);
};

export const getBulkServicesByIds = (data: BulkServiceIdsRequest): Promise<ServiceObject[]> => {
  return httpClient<ServiceObject[]>('/services/bulkservicedetails', HttpMethod.Post, data);
};

export const getNextServiceStatuses = (code: string, serviceType: string, serviceId: number): Promise<NextServiceStatusesResponse> => {
  return httpClient<NextServiceStatusesResponse>(`/possibleNextserviceStatuses/${code}/${serviceType}?serviceId=${serviceId}`, HttpMethod.Get);
};

export const saveServiceStatus = (id: number, status: ServiceStatusChangeRequest): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/${id}/status`, HttpMethod.Put, status);
};

export const getServiceTemplates = (): Promise<ServiceTemplateResponse[]> => {
  return httpClient<ServiceTemplateResponse[]>('/servicetemplates', HttpMethod.Get);
};

export const getOtherServicesUnderMainLocation = (nodeId: number, queryOptions: { page: number; pageSize: number }): Promise<OtherServiceListData> => {
  const { page, pageSize } = queryOptions;
  const url = `/nodes/${nodeId}/otherservices?page=${page + 1}&limit=${pageSize}`;
  return httpClient<OtherServiceListData>(url, HttpMethod.Get);
};

export const getLinkedServices = (serviceId: number): Promise<LinkedServiceData> => {
  return httpClient<LinkedServiceData>(`/services/getlinkedservicesbyserviceid/${serviceId}`, HttpMethod.Get);
};

export const updateParentService = (id: number, parentService: ParentServiceUpdateRequest): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/parentservice/${id}`, HttpMethod.Put, parentService);
};

export const getAllServiceAttendance = (serviceId: number): Promise<ServiceAttendanceObject[]> => {
  return httpClient<ServiceAttendanceObject[]>(`/attendance/${serviceId}`, HttpMethod.Get);
};

export const getCurrentUserAllocatedServices = (locationNodeId: number, serviceId: number): Promise<AllocatedService> => {
  return httpClient<AllocatedService>(`/services/technicians/${locationNodeId}/allocation/${serviceId}`, HttpMethod.Get);
};

export const updateBillingInfo = (serviceId: number, billing: ServiceBillingInfo): Promise<void> => {
  return httpClient<void>(`/services/billingInfo/${serviceId}`, HttpMethod.Put, billing);
};

export const getServicesUnderNodeId = (nodeId: number, includeChildRecords: boolean): Promise<ServiceAccordianData[]> => {
  const url = `/nodes/${nodeId}/services?includeChildRecords=${includeChildRecords}`;
  return httpClient<ServiceAccordianData[]>(url, HttpMethod.Get);
};

export const updateServiceNodeAllocation = (data: ServiceNodeAllocationData): Promise<void> => {
  return httpClient<void>('/servicenodeallocation', HttpMethod.Post, data);
};

export const getEstimatedTimeOfArrival = (queryOptions: EstimatedTimeOfArrivalQuery): Promise<string> => {
  const { nodeId, origin, date, serviceId } = queryOptions;
  const url = `/getEstimatedTimeOfArrival/${nodeId}/${serviceId}/data?origin=${origin}&date=${date}`;
  return httpClient<string>(url, HttpMethod.Get);
};

export const getServicesNodesLocationRelatedAttributes = (serviceId: number, mainLocationNodeId: number, subLocationNodeId: number): Promise<ServicesNodesAttributes> => {
  const url = `/serviceAttributes/${serviceId}/nodeAttributes/mainLocation/${mainLocationNodeId}/subLocation/${subLocationNodeId}`;
  return httpClient<ServicesNodesAttributes>(url, HttpMethod.Get);
};

export const createOrUpdateServiceAttributes = (serviceAttribute: ServiceAttribute): Promise<void> => {
  return httpClient<void>('/serviceAttributesBulkCreateOrUpdate', HttpMethod.Post, serviceAttribute);
};

export const getBillingTypes = (): Promise<BillingType[]> => {
  return httpClient<BillingType[]>('/billingTypes', HttpMethod.Get);
};

export const getServiceCancelOrReopenStatuses = (option: string, statusCode: string, serviceType: string, serviceId: number): Promise<NextServiceStatusesResponse> => {
  const url = `/getServiceCloseOrReOpenStatuses?option=${option}&statusCode=${statusCode}&serviceType=${serviceType}&serviceId=${serviceId}`;
  return httpClient<NextServiceStatusesResponse>(url, HttpMethod.Get);
};

export const getBillingInfo = (serviceId: number): Promise<ServiceBillingInfo> => {
  return httpClient<ServiceBillingInfo>(`/services/billingInfo/${serviceId}`, HttpMethod.Get);
};

export const getServiceActivityMonitorData = (): Promise<ServiceActivityResponseDataType[]> => {
  return httpClient<ServiceActivityResponseDataType[]>('/getServiceActivityMonitor', HttpMethod.Get);
};

export const createServiceBulk = (serviceBulkObj: ServiceBulkCreateRequest): Promise<ServiceObject[]> => {
  return httpClient<ServiceObject[]>('/services/bulk', HttpMethod.Post, serviceBulkObj);
};
